import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Globe2, MapPin, ExternalLink, Wrench, Briefcase, CalendarDays, BadgeCheck, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Tier, Empty } from "@/components/Shared";

export default function CompanyProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notfound, setNotfound] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    api.get(`/companies/${id}`).then((r) => { setData(r.data); setFollowers(r.data.follower_count || 0); }).catch(() => setNotfound(true));
  }, [id]);

  const subscribe = async (e) => {
    e.preventDefault(); setSubscribing(true);
    try {
      const { data: d } = await api.post(`/companies/${id}/follow`, { email });
      setFollowers(d.follower_count); setSubscribed(true); setEmail("");
      toast.success("Subscribed! You'll get updates from this company.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubscribing(false); }
  };

  if (notfound) return <Empty text="Company not found." />;
  if (!data) return <Empty text="Loading…" />;

  const { company, tools, jobs, events } = data;
  const total = tools.length + jobs.length + events.length;

  return (
    <div data-testid="company-profile-page">
      <Seo
        title={`${company.name} — AI Tools, Jobs & Events | WorldAIInfo`}
        description={(company.bio || `${company.name}'s profile on WorldAIInfo${company.country ? ` (${company.country})` : ""} — their AI tools, job openings and events.`).slice(0, 300)}
        image={company.logo_url}
        canonicalPath={`/company/${id}`}
        type="profile"
      />
      <div className="border-b border-white/10 grid-bg">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
          <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00F0FF] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-[#00F0FF]/10 flex items-center justify-center font-display font-extrabold text-[#00F0FF] text-3xl overflow-hidden shrink-0">
              {company.logo_url ? <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover" /> : (company.name || "?")[0]}
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-extrabold text-4xl">{company.name}</h1>
                {company.verified && (
                  <span className="inline-flex items-center gap-1 text-[#00F0FF] font-mono-tech text-xs border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-1 rounded-full" data-testid="company-verified-badge">
                    <BadgeCheck className="w-4 h-4" /> VERIFIED
                  </span>
                )}
              </div>
              {company.bio && <p className="text-[#94A3B8] mt-2 max-w-2xl">{company.bio}</p>}
              <div className="flex flex-wrap gap-3 mt-4 font-mono-tech text-xs text-[#94A3B8]">
                {company.country && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#00F0FF]" /> {company.country}</span>}
                {company.website && <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-[#00F0FF]"><Globe2 className="w-3.5 h-3.5 text-[#00F0FF]" /> Website <ExternalLink className="w-3 h-3" /></a>}
                <span className="flex items-center gap-1.5" data-testid="company-follower-count"><Users className="w-3.5 h-3.5 text-[#00F0FF]" /> {followers} follower{followers === 1 ? "" : "s"}</span>
                <span>{total} active listing{total === 1 ? "" : "s"}</span>
              </div>
              <form onSubmit={subscribe} className="flex gap-2 mt-5 max-w-md">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="company-follow-email"
                  className="flex-1 bg-[#111827] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
                <button disabled={subscribing} data-testid="company-follow-submit"
                  className="bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition text-sm disabled:opacity-50">
                  {subscribed ? "Following" : subscribing ? "…" : "Follow"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 space-y-14">
        {tools.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-2xl mb-5 flex items-center gap-2"><Wrench className="w-5 h-5 text-[#00F0FF]" /> Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {tools.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="card-surface rounded-xl p-5" data-testid={`company-tool-${t.id}`}>
                  <div className="flex items-center justify-between mb-2"><Link to={`/tools/${t.id}`} className="font-display font-bold hover:text-[#00F0FF]">{t.name}</Link><Tier tier={t.tier} /></div>
                  <p className="text-sm text-[#00F0FF]">{t.tagline}</p>
                  <p className="text-sm text-[#94A3B8] mt-1 line-clamp-2">{t.description}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}
        {jobs.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-2xl mb-5 flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#00F0FF]" /> Open Roles</h2>
            <div className="flex flex-col gap-3">
              {jobs.map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="card-surface rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`company-job-${j.id}`}>
                  <div><span className="font-display font-bold">{j.title}</span><p className="text-sm text-[#94A3B8]">{j.location} · {j.country}{j.remote ? " · Remote" : ""}</p></div>
                  {j.salary && <span className="font-display font-bold text-[#00F0FF] text-sm">{j.salary}</span>}
                </Link>
              ))}
            </div>
          </section>
        )}
        {events.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-2xl mb-5 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#00F0FF]" /> Events</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((e) => (
                <div key={e.id} className="card-surface rounded-xl p-5" data-testid={`company-event-${e.id}`}>
                  <span className="font-mono-tech text-[10px] uppercase text-[#4da3ff]">{e.event_type}</span>
                  <h3 className="font-display font-bold mt-1">{e.name}</h3>
                  <p className="text-sm text-[#94A3B8]">{e.location}, {e.country}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        {total === 0 && <Empty text="This company has no active listings yet." />}
      </div>
    </div>
  );
}
